import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, CardContent, TextField, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import { useAnalytics } from '../../../../api/analytics';
import CustomCircularProgress from '../../../../components/ui/customCircularProgress/customCircularProgress.component';
import CustomAutocomplete from '../../../../components/ui/customAutocomplete/customAutocomplete.component';
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import StatCard from '../../../../components/ui/statCard/statCard.component';
import ServiceDistributionCharts from '../../../../components/service/serviceDistributionCharts/serviceDistributionCharts.component';
import { useHasPermission } from '../../../../contexts/auth';
import {
  BREAKDOWN_JOB_ISSUES_ENDPOINT,
  BREAKDOWN_JOB_SERVICES_ENDPOINT,
  BREAKDOWN_USED_PARTS_ENDPOINT,
  EQUIPMENT_GROUPS_ENDPOINT,
} from '../../../../consts';
import { EquipmentGroupServiceStats } from '../../../../types/analytics';
import DateRangeFilter from '../../../../components/ui/dateRangeFilter/dateRangeFilter.component';
import { useDateRangeQueryParams } from '../../../../hooks/useDateRangeQueryParams';
import { buildUsedPartsBreakdownColumns, formatUsedPartsTotals } from '../../../../utils/partDisplay';
import { buildJobServiceValueColumn, formatJobServicesTotals } from '../../../../utils/jobServiceDisplay';
import { formatCounterValue } from '../../../../utils';

interface EquipmentGroup {
  id: string;
  name: string;
  [key: string]: any;
}

export const GroupStats = () => {
  const { t } = useTranslation();

  const jobIssueColumns: GridColDef[] = [
    { field: 'title', headerName: t('Issue'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
  ];

  const jobServiceColumns: GridColDef[] = [
    { field: 'title', headerName: t('Service'), flex: 1, minWidth: 160 },
    { field: 'count', headerName: t('Count'), width: 120 },
    buildJobServiceValueColumn(t),
  ];

  const usedPartColumns = buildUsedPartsBreakdownColumns(t);

  const canViewJobs = useHasPermission('service.view_job');
  const { loading, error, getEquipmentGroupServiceStats } = useAnalytics();
  const [groups, setGroups] = useState<EquipmentGroup[]>([]);
  const [group, setGroup] = useState<EquipmentGroup | null>(null);
  const { dateRange, setDateRange } = useDateRangeQueryParams();
  const [stats, setStats] = useState<EquipmentGroupServiceStats | null>(null);

  const handleOptionsChange = useCallback((fetchedOptions: EquipmentGroup[]) => {
    setGroups((prev) => {
      const merged = [...prev];
      fetchedOptions.forEach((option) => {
        if (!merged.find((existing) => existing.id === option.id)) {
          merged.push(option);
        }
      });
      return merged;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      if (!group || !canViewJobs) {
        setStats(null);
        return;
      }
      const data = await getEquipmentGroupServiceStats(group.id, dateRange);
      if (!cancelled) {
        setStats(data || null);
      }
    };

    fetchStats();
    return () => {
      cancelled = true;
    };
  }, [group, dateRange, canViewJobs, getEquipmentGroupServiceStats]);

  const scopeParams = useMemo(
    () =>
      group
        ? {
            group_id: group.id,
            start_date: dateRange.start_date,
            end_date: dateRange.end_date,
          }
        : undefined,
    [group, dateRange.start_date, dateRange.end_date]
  );
  const scopeKey = `${group?.id}-${dateRange.start_date}-${dateRange.end_date}`;

  if (!canViewJobs) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 1 }}>
      <Card>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CustomAutocomplete
            size="small"
            apiConfig={{ url: EQUIPMENT_GROUPS_ENDPOINT, minSearchChars: 0, debounceMs: 300 }}
            options={groups}
            onOptionsChange={handleOptionsChange}
            value={group}
            onChange={(_, newValue) => setGroup((newValue as EquipmentGroup | null) || null)}
            isOptionEqualToValue={(option, selectedValue) =>
              String(option.id) === String((selectedValue as EquipmentGroup).id)
            }
            getOptionLabel={(option) => (option as EquipmentGroup).name || ''}
            noOptionsText={t('No options')}
            renderInput={(params) => <TextField {...params} label={t('Equipment Group')} />}
          />
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </CardContent>
      </Card>

      {!group && (
        <Alert severity="info">{t('Select an equipment group to see its statistics')}</Alert>
      )}

      {group && loading && !stats && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CustomCircularProgress />
        </Box>
      )}

      {group && error && (
        <Alert severity="error">{t('There was an error, please review the form.')}</Alert>
      )}

      {group && stats && scopeParams && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {stats.group_name}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <StatCard label={t('Equipments')} value={stats.equipment_unit_count} />
            <StatCard label={t('Jobs in Selected Period')} value={stats.jobs_in_range} />
            <StatCard
              label={t('Used Parts')}
              value={formatUsedPartsTotals(stats.used_parts)}
            />
            <StatCard label={t('Job Issues')} value={stats.job_issues_count} />
            <StatCard
              label={t('Job Services')}
              value={formatJobServicesTotals(stats.job_services_count, stats.job_services_value)}
            />
            <StatCard label={t('Black pages')} value={formatCounterValue(stats.black_pages)} />
            {stats.has_color_equipment && (
              <StatCard label={t('Color pages')} value={formatCounterValue(stats.color_pages)} />
            )}
            <StatCard label={t('Scanned pages')} value={formatCounterValue(stats.scanned_pages)} />
          </Box>

          <ServiceDistributionCharts
            stats={stats}
            loading={loading}
            hasColorEquipment={stats.has_color_equipment}
          />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Job Issues')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_JOB_ISSUES_ENDPOINT}
                  columns={jobIssueColumns}
                  extraParams={scopeParams}
                  dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('Job Services')}
                </Typography>
                <CustomDataGrid
                  key={scopeKey}
                  url={BREAKDOWN_JOB_SERVICES_ENDPOINT}
                  columns={jobServiceColumns}
                  extraParams={scopeParams}
                  dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
                />
              </CardContent>
            </Card>
          </Box>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('Used Parts')}
              </Typography>
              <CustomDataGrid
                key={scopeKey}
                url={BREAKDOWN_USED_PARTS_ENDPOINT}
                columns={usedPartColumns}
                extraParams={scopeParams}
                dataGridProps={{ autoHeight: true, pageSizeOptions: [5, 10, 25], disableColumnFilter: true }}
              />
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};
